import type { TilesConfig } from "@repo/lib";

type T = (
  k: string,
  o?: { fallback?: string; vars?: Record<string, string> },
) => string;

// = @repo/lib tileLabel, inlined so this module (and its jest test) has no runtime deps
const tileLabel = (
  t: T,
  item: { label?: string; labelTerm?: string },
): string =>
  item.labelTerm
    ? t(item.labelTerm, { fallback: item.label ?? "" })
    : (item.label ?? "");

export type Floor = {
  mapName: string;
  floor: number;
  label: string;
  rawLabel: string;
  ownLabel: string | null;
};
export type Area = { group: string; label: string; floors: Floor[] };

/** Group the interiors of `parentMap` into areas → floors (see LayerSelect). */
export function buildAreas(
  tiles: TilesConfig,
  parentMap: string,
  t: T,
): Area[] {
  const byGroup = new Map<string, Area>();
  for (const [mapName, cfg] of Object.entries(tiles)) {
    const layer = cfg.layer;
    if (!layer || layer.parent !== parentMap) continue;
    let a = byGroup.get(layer.group);
    if (!a) {
      a = {
        group: layer.group,
        label: tileLabel(t, layer) || t(mapName) || mapName,
        floors: [],
      };
      byGroup.set(layer.group, a);
    }
    a.floors.push({
      mapName,
      floor: layer.floor,
      label: t("layers.floor", { vars: { floor: String(layer.floor) } }),
      rawLabel: layer.label ?? "",
      // a per-floor term ("Underground", "2F" / "Sous-sol", "2e étage")
      ownLabel: layer.labelTerm ? tileLabel(t, layer) : null,
    });
  }
  const areas = [...byGroup.values()];
  for (const a of areas) {
    a.floors.sort((x, y) => x.floor - y.floor);
    // Floors labelled per floor (Aniimo "Underground"/"1F", Delta Force "Floor 1")
    // carry no area name: the first floor's label would name the whole area.
    if (new Set(a.floors.map((f) => f.rawLabel)).size > 1) {
      a.label = t("layers.layeredMap");
      for (const f of a.floors) if (f.ownLabel) f.label = f.ownLabel;
    }
    // a one-floor area (a planet's single "Underground" map) is named, not "Floor 1"
    if (a.floors.length === 1) a.floors[0]!.label = a.label;
  }
  areas.sort((a, b) => a.label.localeCompare(b.label));
  return areas;
}
